import { useState } from 'react';

import { createForm, useFormContext } from '@components/form';
import { IconForm } from '@components/icons/form';
import { MaxLengthCounter } from '@components/max-length-counter';
import { MaxLengthCounterProps } from '@components/max-length-counter/max-length-counter';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

const Form = createForm<{ name: string; description: string }>();

export const EditFormDetailsSidebar = ({
  name,
  description,
  onSave,
}: {
  name: string;
  description: string;
  onSave({ name, description }: { name: string; description: string }): void;
}) => {
  const { t } = useScopedTranslate(locale);

  const [isCharacterLimitExceeded, setIsCharacterLimitExceeded] = useState(false);

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('formDetails')} icon={IconForm} iconColor="elementBlue100" />
      <Sidebar.Content>
        <Form onSubmit={onSave}>
          <Form.TextField id="name" label={t('name')} maxLength={50} defaultValue={name} />
          <Description
            onExceedStatusChange={setIsCharacterLimitExceeded}
            defaultValue={description}
          />
          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
              <Form.Submit
                text={t('core.save')}
                variant="primary-blue"
                isDisabled={isCharacterLimitExceeded}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const Description = ({
  onExceedStatusChange,
  defaultValue,
}: {
  onExceedStatusChange: MaxLengthCounterProps['onExceedStatusChange'];
  defaultValue: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const {
    store: { useFieldValue },
  } = useFormContext();
  const description = (useFieldValue('description') || '') as string;

  return (
    <Stack space={1}>
      <Form.Textarea id="description" label={t('description')} defaultValue={defaultValue} />
      <MaxLengthCounter
        currentLength={description.length}
        maxLength={255}
        onExceedStatusChange={onExceedStatusChange}
      />
    </Stack>
  );
};

const locale = {
  en: {
    description: 'Description',
    formDetails: 'Form details',
    name: 'Name',
  },
};

export const useEditFormDetailsSidebar = () => {
  return useSidebar(EditFormDetailsSidebar, {
    variant: 'REGULAR',
  });
};
